import { AlertErrorCircle } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { Carousel } from '@/components/carousel/Carousel'
import { DraftCard, DraftCardSkeleton } from '@/drafting/components/DraftCard'
import { useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { selectArtifactById } from '@/artifacts/store/selectors'
import { selectConversationIdForDraftArtifactVersion } from '@/drafting/store/selectors'
import { kUntitledDraftName } from '@/drafting/constants'
import { selectMatterById } from '@/matter/store/selectors'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import { useCreateNewAgentChat } from '@/agent/conversations/hooks/useNewAgentChat'
import { useDelayedVisibility } from '@/hooks/useDelayedVisibility'
import { useMatterDrafts } from '@/matter/hooks/useMatterDrafts'
import { useNavigateToDraftInChat } from '@/drafting/hooks/useNavigateToDraftInChat'
import { DraftEntryPoint } from '@/analytics/enums/draft.enums'

interface DraftCarouselItemProps {
  draftArtifactVersionId: string
}

/**
 * A single draft card in the DraftCarousel
 */
function DraftCarouselItem({ draftArtifactVersionId }: DraftCarouselItemProps) {
  const artifact = useAppSelector((state: RootState) => selectArtifactById(state, { artifactVersionId: draftArtifactVersionId }))
  const conversationId = useAppSelector((state: RootState) => selectConversationIdForDraftArtifactVersion(state, { draftArtifactVersionId }))
  const navigateToDraftInChat = useNavigateToDraftInChat()

  if (!artifact || !conversationId) return null

  const name = artifact.name ?? kUntitledDraftName

  return (
    <DraftCard
      name={name}
      onClick={() =>
        navigateToDraftInChat({ artifactVersionId: draftArtifactVersionId, conversationId, draftEntryPoint: DraftEntryPoint.RecentDraftsCarousel })
      }
    />
  )
}

/** Max drafts shown in the Recent Drafts section. */
const MAX_RECENT_DRAFTS = 12

/** Number of placeholder cards shown while a matter's drafts are first loading. */
const LOADING_SKELETON_CARD_COUNT = 3

/**
 * A horizontal carousel of the active matter's drafts
 */
export function DraftCarousel() {
  const { createNewAgentChat } = useCreateNewAgentChat()
  const { matterId, draftArtifactVersionIds, isError, isLoadingWithoutCachedDrafts, refetch } = useMatterDrafts()
  const matter = useAppSelector((state: RootState) => selectMatterById(state, { matterId }))
  const isMatterUnassigned = isUnassignedMatter(matter)

  // Show the skeleton as soon as the fetch starts and hold it for a short minimum, so a fast fetch doesn't introduce a jarring flash
  // Drafts are fetched per matter, so this shows on the first visit to each matter (revisits are cached)
  const withinMinimumDisplay = useDelayedVisibility(isLoadingWithoutCachedDrafts, { showDelayMs: 0, minDisplayMs: 500 })
  const showSkeletonCards = isLoadingWithoutCachedDrafts || withinMinimumDisplay

  // Show the error state only when the fetch failed with nothing to fall back on
  // A failed background refresh that still has cached drafts keeps showing them (matches the loading behavior)
  const showError = isError && draftArtifactVersionIds.length === 0 && !showSkeletonCards

  if (!matterId) return null

  // Hide the whole section for the unassigned matter when it has no drafts
  // The unassigned matter is read-only and has no way to start a chat, so an empty drafts carousel there has nothing actionable to offer
  if (isMatterUnassigned && draftArtifactVersionIds.length === 0) return null

  return (
    <div className="mt-6">
      <h4 className="font-rethink-sans text-base font-medium leading-6 tracking-normal text-brand-neutral-950 mb-3">Recent Drafts</h4>
      <Carousel
        ariaLabel="Drafts"
        // Skeleton placeholders aren't navigable, so disable scrolling and the arrows
        disableInteraction={showSkeletonCards}
        noItemsSlot={
          showError ? (
            <div className="flex flex-col items-center gap-2">
              <span className="flex items-center gap-1.5 text-true-gray-700">
                <AlertErrorCircle className="size-3.5 shrink-0" />
                {/* Shorter copy below the sm breakpoint so the message stays on one line on mobile */}
                <span className="sm:hidden">Failed to load drafts</span>
                <span className="hidden sm:inline">There was an error loading your drafts</span>
              </span>
              <Button variant="outlined" size="xs" onClick={refetch}>
                Reload drafts
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <span>No drafts in this matter yet</span>
              <button
                type="button"
                onClick={() => createNewAgentChat(true, matterId)}
                className="font-medium underline text-brand-700 transition-colors duration-150 hover:text-brand-800 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
              >
                Start a new chat
              </button>
            </div>
          )
        }
      >
        {showSkeletonCards
          ? Array.from({ length: LOADING_SKELETON_CARD_COUNT }, (_, i) => <DraftCardSkeleton key={i} />)
          : draftArtifactVersionIds.slice(0, MAX_RECENT_DRAFTS).map((id) => <DraftCarouselItem key={id} draftArtifactVersionId={id} />)}
      </Carousel>
    </div>
  )
}
